import { type ReactNode } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type AssistantContentOptions = {
  /** should the content only be shown for the specific location at the time of the call? */
  isTiedToCurrentLocation?: boolean;
  /** optional static content which renders below the returned content */
  contentFooter?: ReactNode;
  /** the pathname that content or contentStatic is tied to */
  location?: string;
};

export type AssistantStoreState = {
  /** is the assistant open? */
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  /** is the assistant not ready to interact with yet? */
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  /** label to show next to the loading state */
  loadingStateLabel: string;
  setLoadingStateLabel: (label: string) => void;
  /** is some async/background action going to affect or is currently affecting the assistant? */
  isBusy: boolean;
  setIsBusy: (isBusy: boolean) => void;
  /** is the assistant enabled? */
  isDisabled: boolean;
  setIsDisabled: (isDisabled: boolean) => void;
  /** has the content been seen within the assistant? */
  isSeen: boolean;
  /** set if the content has been seen within the assistant */
  setIsSeen: (isSeen: boolean) => void;
  /** the markdown content to show within the assistant */
  content: string | null;
  /** set the markdown content synchronously. triggers the loading state. */
  setContent: (content: string | null, opts?: AssistantContentOptions) => void;
  /** set the markdown content asynchronously. triggers the loading state. */
  setContentAsync: (
    op: () => Promise<string | null>,
    opts?: AssistantContentOptions
  ) => Promise<void>;
  /** the static content to show within the assistant */
  contentStatic: ReactNode | null;
  /** set the static content synchronously. triggers the loading state. */
  setContentStatic: (
    content: ReactNode | null,
    opts?: AssistantContentOptions
  ) => void;
  /** set the static content asynchronously. triggers the loading state. */
  setContentStaticAsync: (
    op: () => Promise<ReactNode | null>,
    opts?: AssistantContentOptions
  ) => Promise<void>;
  /** boolean that indicates where the set content has been fully revealed yet through animation */
  isContentRevealed: boolean;
  /** change flag indicating if the content has been fully revealed or not */
  setIsContentRevealed: (isContentRevealed: boolean) => void;
  /** footer to be shown when content is done loading */
  contentFooter: ReactNode;
  /** reset the content and associated state */
  clearContent: () => void;
  /** error related to getting or generating the content of the assistant */
  error?: unknown;
  /** should the content only be shown for the specific location at the time of the call? */
  isContentTiedToCurrentLocation: boolean;
  /** the pathname that the content is tied to (includes the params) */
  contentTiedToLocation?: string;
  /** set the pathname that the content is tied to (includes the params) */
  setContentTiedToLocation: (location: string) => void;
  /** should the content only be shown for the specific location at the time of the call? */
  isContentStaticTiedToCurrentLocation: boolean;
  /** the pathname that the content is tied to (includes the params) */
  contentStaticTiedToLocation?: string;
  /** set the pathname that the content is tied to (includes the params) */
  setContentStaticTiedToLocation: (location: string) => void;
  hasHydrated: boolean;
  setHasHydrated: (isHydrated: boolean) => void;
};

/** @deprecated Validere Assistant summarization store; superseded by Stratos. */
export const useAssistant = create<AssistantStoreState>()(
  persist(
    (set, get) => ({
      isOpen: false,
      setIsOpen: (isOpen: boolean) => set({ isOpen }),
      isLoading: false,
      setIsLoading: (isLoading: boolean) => set({ isLoading }),
      loadingStateLabel: "",
      setLoadingStateLabel: (label: string) =>
        set({ loadingStateLabel: label }),
      isBusy: false,
      setIsBusy: (isBusy: boolean) => set({ isBusy }),
      isDisabled: false,
      setIsDisabled: (isDisabled: boolean) => set({ isDisabled }),
      isSeen: false,
      setIsSeen: (isSeen: boolean) => set({ isSeen }),
      content: null,
      setContent: (content: string | null, opts) =>
        set({
          content,
          isSeen: false,
          isContentTiedToCurrentLocation: opts?.isTiedToCurrentLocation,
          isContentRevealed: false,
          contentFooter: opts?.contentFooter,
          contentTiedToLocation: opts?.location ?? "",
        }),
      setContentAsync: async (
        op: (stateGetter: () => AssistantStoreState) => Promise<string | null>,
        opts
      ) => {
        set({
          isLoading: true,
          isSeen: false,
          isContentTiedToCurrentLocation: opts?.isTiedToCurrentLocation,
          isContentRevealed: false,
          contentFooter: opts?.contentFooter,
          contentTiedToLocation: opts?.location ?? "",
        });

        return op(get)
          .then((content) => {
            set({
              content,
              isSeen: false,
            });
          })
          .catch((error) => set({ error }))
          .finally(() => {
            set({ isLoading: false });
          });
      },
      contentStatic: null,
      setContentStatic: (content: ReactNode | null, opts) =>
        set({
          contentStatic: content,
          isSeen: false,
          contentStaticTiedToLocation: opts?.location ?? "",
          isContentStaticTiedToCurrentLocation: opts?.isTiedToCurrentLocation,
        }),
      setContentStaticAsync: async (
        op: (
          stateGetter: () => AssistantStoreState
        ) => Promise<ReactNode | null>,
        opts
      ) => {
        set({
          isLoading: true,
          isSeen: false,
          contentStaticTiedToLocation: opts?.location ?? "",
          isContentStaticTiedToCurrentLocation: opts?.isTiedToCurrentLocation,
        });

        return op(get)
          .then((content) => {
            set({
              contentStatic: content,
              isSeen: false,
            });
          })
          .catch((error) => set({ error }))
          .finally(() => {
            set({ isLoading: false });
          });
      },
      isContentRevealed: false,
      setIsContentRevealed: (isContentRevealed: boolean) =>
        set({ isContentRevealed: isContentRevealed }),
      contentFooter: null,
      clearContent: () =>
        set({
          content: null,
          isSeen: false,
          error: undefined,
          isContentTiedToCurrentLocation: false,
          contentTiedToLocation: undefined,
          loadingStateLabel: "",
          isContentRevealed: false,
        }),
      isContentTiedToCurrentLocation: false,
      setContentTiedToLocation: (location: string) =>
        set({ contentTiedToLocation: location }),
      isContentStaticTiedToCurrentLocation: false,
      setContentStaticTiedToLocation: (location: string) =>
        set({ contentStaticTiedToLocation: location }),
      hasHydrated: false,
      setHasHydrated: (state) => {
        set({
          hasHydrated: state,
        });
      },
    }),
    {
      name: "assistant", // will be scoped to the user session in AuthenticatedContext
      storage: createJSONStorage(() => localStorage),
      skipHydration: true,
      partialize: (state) => {
        return Object.fromEntries(
          Object.entries(state).filter(
            ([key]) =>
              !["contentStatic", "contentFooter", "isOpen"].includes(key)
          )
        );
      },
    }
  )
);
